import { useState, type ReactNode } from "react";
import {
  Alert,
  Checkbox,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import type { Promisable } from "type-fest";

import { CancelOrProceedActions } from "../../components/CancelOrProceedActions/CancelOrProceedActions.js";

export type ConfirmChangesDialogProps = {
  open: boolean;
  title: string;
  confirmLabel: string;
  saving: boolean;
  error: string | null;
  onCancel: () => Promisable<void>;
  onConfirm: () => Promisable<void>;
  /**
   * What the administrator ticks to say they understand, where the change
   * alters who can see or admin young people. Absent where it does not, so a
   * tick never becomes a habit clicked through.
   */
  acknowledgement?: string;
  /** True while what the change does is still being worked out; confirming waits for it. */
  confirmWaits?: boolean;
  children: ReactNode;
};

/**
 * The last step before unsaved changes apply: what they are and what they apply to.
 * Cancelling applies nothing and leaves the changes unsaved.
 */
export const ConfirmChangesDialog = ({
  open,
  title,
  confirmLabel,
  saving,
  error,
  onCancel,
  onConfirm,
  acknowledgement,
  confirmWaits = false,
  children,
}: ConfirmChangesDialogProps) => {
  const { t } = useTranslation();
  const [acknowledged, setAcknowledged] = useState(false);

  return (
    <Dialog
      open={open}
      onClose={() => {
        if (!saving) {
          void onCancel();
        }
      }}
      // Cleared each time it closes, so a tick given for one change is never
      // carried over to the next.
      slotProps={{
        transition: {
          onExited: () => {
            setAcknowledged(false);
          },
        },
      }}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          {error !== null && <Alert severity="error">{error}</Alert>}
          {children}
          {acknowledgement === undefined ? null : (
            <FormControlLabel
              control={
                <Checkbox
                  checked={acknowledged}
                  onChange={(event) => {
                    setAcknowledged(event.target.checked);
                  }}
                />
              }
              label={acknowledgement}
            />
          )}
        </Stack>
      </DialogContent>
      <CancelOrProceedActions
        cancel={{
          label: t("admin.configuration.settings.unsaved.cancel"),
          onClick: onCancel,
          disabled: saving,
        }}
        proceed={{
          label: confirmLabel,
          onClick: onConfirm,
          disabled:
            saving ||
            confirmWaits ||
            (acknowledgement !== undefined && !acknowledged),
          kind: "primary",
        }}
      />
    </Dialog>
  );
};
